import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';

import clsx from 'clsx';
import { matchSorter } from 'match-sorter';

import {
    ADD_ITEM,
    REMOVE_ITEM,
    SELECT_ALL,
    SELECT_ONLY_ONE,
    SET_FILTERED_DATA,
    SET_INPUT_VALUE,
    UNSELECT_ALL
} from '@/utils/hooks/context/searchBox/Actions';
import type {
    InitialContextType,
    SearchBoxItem
} from '@/utils/hooks/context/searchBox/contextConfig';
//hooks
import {
    SearchBoxProvider,
    useSearchBoxContext
} from '@/utils/hooks/context/searchBox/useSearchBoxContext';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import type { CheckboxProps } from '@/components/base/Checkbox';
import Checkbox from '@/components/base/Checkbox';
//components
import Icon from '@/components/base/Icon';
//types
import type { InputProps } from '@/components/base/Input';
import Input from '@/components/base/Input';
import Loader from '@/components/base/Loader';
import type { RadioProps } from '@/components/base/Radio';
import Radio from '@/components/base/Radio';
import type { TooltipProps } from '@/components/base/Tooltip';
import Tooltip from '@/components/base/Tooltip';

type SearchBoxProps<TItem extends SearchBoxItem> = {
    children: ReactNode;
} & Omit<InitialContextType<TItem>, 'filteredData' | 'selectedItems'>;

export function SearchBox<TItem extends SearchBoxItem>({
    children,
    initialData,
    searchKeys,
    inputId,
    globalError,
    disabled = false,
    isMultiSelect = true
}: SearchBoxProps<TItem>) {
    return (
        <div className="flex flex-col space-y-1">
            <SearchBoxProvider<TItem>
                value={{
                    initialData, // initial data that will be the reference
                    globalError,
                    selectedItems: initialData?.filter((item) => item.selected), // selected items
                    filteredData: initialData, // filteredOptions available to the user
                    searchKeys,
                    inputId,
                    disabled,
                    isMultiSelect
                }}>
                {children}
            </SearchBoxProvider>
            {globalError ? (
                <div
                    className="mt-1 w-full font-GMedium text-xs text-icoRed"
                    data-testid="global-error">
                    {globalError}
                </div>
            ) : null}
        </div>
    );
}

type WrapperProps<TItem extends SearchBoxItem> = RequiredFor<
    ComponentPropsWithoutRef<'div'>,
    'children'
> & {
    withoutStyle?: boolean;
    onSelectionChange?: (arg: TItem[]) => void;
};

const Wrapper = <TItem extends SearchBoxItem>({
    children,
    onSelectionChange,
    withoutStyle = false,
    className,
    ...restProps
}: WrapperProps<TItem>) => {
    const [isMounted, setIsMounted] = useState(false);

    const { dataContext } = useSearchBoxContext<TItem>();
    //update external state when selectedItems change
    useEffect(() => {
        if (typeof onSelectionChange === 'function' && isMounted) {
            return onSelectionChange(dataContext.selectedItems);
        }

        setIsMounted(true);
    }, [JSON.stringify(dataContext.selectedItems)]);

    return (
        <div
            className={
                withoutStyle
                    ? className
                    : clsx(
                          'flex flex-col rounded-md bg-white px-1 py-5 dark:bg-icoDarkMode-deepBlack sm:px-3 sm:py-8',
                          className,
                          {
                              'border border-icoRed dark:border-icoRed': dataContext.globalError,
                              'border border-icoGray-300 dark:border-transparent':
                                  !dataContext.globalError
                          }
                      )
            }
            {...restProps}>
            {children}
        </div>
    );
};

SearchBox.Wrapper = Wrapper;

const checkIsAllSelected = <TItem extends SearchBoxItem>(filteredData: TItem[]) => {
    return Boolean(!(filteredData ?? [])?.find((item) => !item.selected && !item.disabled));
};

type SearchBoxInputProps = ComponentPropsWithoutRef<'input'> &
    Partial<Pick<InputProps, 'inputStyle' | 'noBorder' | 'noBorderShadow'>> & {
        wrapperClassName?: string;
    };
const SearchBoxInput = <TItem extends SearchBoxItem>({
    placeholder,
    inputStyle = 'search',
    noBorder = true,
    wrapperClassName,
    ...restProps
}: SearchBoxInputProps) => {
    const { dataContext, dispatch } = useSearchBoxContext<TItem>();
    const { searchKeys, disabled, initialData, inputId } = dataContext;
    const [searchValue, setSearchValue] = useState('');

    const getFilteredData = (searchValue: string) => {
        if (!searchValue) {
            return initialData;
        }
        return matchSorter(initialData, searchValue, searchKeys);
    };

    useDebounce(
        () => {
            const newFilteredData = getFilteredData(searchValue);
            dispatch({ type: SET_FILTERED_DATA, payload: newFilteredData });
            dispatch({ type: SET_INPUT_VALUE, payload: searchValue });
        },
        200,
        [searchValue]
    );

    return (
        <div className={wrapperClassName ?? 'pl-2 pr-4 sm:px-5'}>
            <Input
                data-testid="search-input"
                id={`search-${inputId}`}
                inputStyle={inputStyle}
                disabled={disabled}
                noBorder={noBorder}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={placeholder ?? null}
                beforeInput={<Icon icon="search" />}
                {...restProps}
            />
        </div>
    );
};

SearchBox.Input = SearchBoxInput;

type SearchBoxFilteredItemsProps = React.ComponentPropsWithoutRef<'div'> & {
    noDataMessage?: ReactNode;
    hideSelectAll?: boolean;
};
const SearchBoxFilteredItems = <TItem extends SearchBoxItem>({
    children,
    noDataMessage,
    className,
    hideSelectAll = false,
    ...restProps
}: SearchBoxFilteredItemsProps) => {
    const { t } = useTranslation('commons');
    const { dataContext, dispatch } = useSearchBoxContext<TItem>();
    const { disabled, filteredData, isMultiSelect } = dataContext;

    const isAllSelected = checkIsAllSelected(filteredData);

    if (!filteredData.length) {
        return (
            <div
                className="scrollbar flex flex-1 flex-col items-center justify-center pl-2 pr-4 sm:mt-5 sm:px-5"
                data-testid="no-options">
                <div className="text-center text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {noDataMessage ?? t('no_options_to_choose_from')}
                </div>
            </div>
        );
    }

    return (
        <>
            {isMultiSelect && !hideSelectAll ? (
                <div className="py-m pl-2 pr-4 text-right sm:px-5">
                    <button
                        type="button"
                        data-testid={
                            isAllSelected ? 'search-box-unselect-all' : 'search-box-select-all'
                        }
                        disabled={disabled}
                        onClick={() =>
                            dispatch({ type: isAllSelected ? UNSELECT_ALL : SELECT_ALL })
                        }
                        className="font-GSemiBold text-xs text-icoBlue">
                        {isAllSelected ? t('unselect_all') : t('select_all')}
                    </button>
                </div>
            ) : null}
            <div
                className={clsx(
                    'scrollbar flex flex-1 flex-col gap-3 py-1 pl-2 pr-4 sm:px-5',
                    className
                )}
                {...restProps}>
                {children}
            </div>
        </>
    );
};

SearchBox.ListsItems = SearchBoxFilteredItems;

export type SearchBoxItemProps<TItem extends SearchBoxItem> = RequiredFor<
    ComponentPropsWithoutRef<'div'>,
    'children'
> & {
    disabled?: boolean;
    item: TItem;
    tooltipProps?: Omit<TooltipProps, 'children'>;
    boxStyle?: Extract<CheckboxProps['boxStyle'], RadioProps['boxStyle']>;
};
const SearchItem = <TItem extends SearchBoxItem>({
    children,
    disabled = false,
    item,
    className,
    boxStyle = 'default',
    tooltipProps,
    ...restProps
}: SearchBoxItemProps<TItem>) => {
    const { dataContext, dispatch } = useSearchBoxContext<TItem>();
    const { disabled: searchBoxDisabled, isMultiSelect } = dataContext;

    return (
        <div
            className={clsx('flex items-center justify-between space-x-3.5', className)}
            {...restProps}>
            {children}
            <Tooltip {...tooltipProps}>
                <span className="flex items-center">
                    {isMultiSelect ? (
                        <Checkbox
                            id={typeof item.id === 'number' ? item.id.toString() : item.id}
                            onChange={() =>
                                dispatch({
                                    type: item.selected ? REMOVE_ITEM : ADD_ITEM,
                                    payload: item
                                })
                            }
                            boxStyle={boxStyle}
                            checked={item.selected}
                            disabled={searchBoxDisabled || disabled}
                            data-testid={`item-${item.id}`}
                        />
                    ) : (
                        <Radio
                            id={typeof item.id === 'number' ? item.id.toString() : item.id}
                            onChange={() =>
                                dispatch({
                                    type: SELECT_ONLY_ONE,
                                    payload: item
                                })
                            }
                            boxStyle={boxStyle}
                            checked={item.selected}
                            disabled={searchBoxDisabled || disabled}
                            data-testid={`item-${item.id}`}
                        />
                    )}
                </span>
            </Tooltip>
        </div>
    );
};

SearchBox.Item = SearchItem;

type LoadingSearchBoxProps = ComponentPropsWithoutRef<'div'>;
export const LoadingSearchBox = ({ className, ...restProps }: LoadingSearchBoxProps) => {
    return (
        <div
            className={clsx(
                'rounded-md border border-icoGray-300 bg-white px-1 py-5 dark:border-transparent dark:bg-icoDarkMode-deepBlack sm:px-3 sm:py-8',
                className
            )}
            {...restProps}>
            <div className="pl-2 pr-4 sm:px-5">
                <Input
                    data-testid="search-input"
                    inputStyle="search"
                    noBorder={true}
                    disabled
                    beforeInput={<Icon icon="search" />}
                />
            </div>
            <div className="scrollbar flex max-h-60 flex-col space-y-4 py-1 pl-2 pr-4 sm:mt-5 sm:max-h-64 sm:px-5">
                <div
                    className="scrollbar flex h-40 flex-col items-center justify-center space-y-4 pl-2 pr-4 sm:mt-5 sm:px-5"
                    data-testid="loading-options">
                    <Loader className="h-5 w-5 text-icoBlue" />
                </div>
            </div>
        </div>
    );
};
